import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Avatar, 
  Button, 
  Paper,
  Rating,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import { 
  getAuth, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  addDoc,
  updateDoc
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import AddIcon from '@mui/icons-material/Add';
import ChatSection from '../components/ChatSection';
import DressingGrid from '../components/DressingGrid';
import UserRatingStats from '../components/UserRatingStats';

interface UserProfile {
  firstName: string;
  lastName: string;
  location: string;
  rating: number;
  ratingCount: number;
  photoURL?: string;
}

interface Dress {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  availableDate: string;
  location: string;
}

const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [dresses, setDresses] = useState<Dress[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAddDress, setOpenAddDress] = useState(false);
  const [newDress, setNewDress] = useState({
    title: '',
    description: '',
    availableDate: '',
    location: ''
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }
      setUser(user);
      await loadUserProfile(user.uid);
      await loadUserDresses(user.uid);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadUserProfile = async (uid: string) => {
    const db = getFirestore();
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      setProfile(userDoc.data() as UserProfile);
    }
  };

  const loadUserDresses = async (uid: string) => {
    const db = getFirestore();
    const q = query(collection(db, 'dresses'), where('userId', '==', uid));
    const querySnapshot = await getDocs(q);
    const dressesList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Dress[];
    setDresses(dressesList);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !user) return;
    
    const file = event.target.files[0];
    const storage = getStorage();
    const storageRef = ref(storage, `profile_images/${user.uid}`);
    
    try {
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update user profile
      const auth = getAuth();
      await updateProfile(auth.currentUser!, { photoURL: downloadURL });
      
      // Update Firestore
      const db = getFirestore();
      await updateDoc(doc(db, 'users', user.uid), {
        photoURL: downloadURL
      });
      
      setProfile(prev => prev ? { ...prev, photoURL: downloadURL } : null);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleAddDress = async () => {
    if (!user || !selectedImage) return;

    const storage = getStorage();
    const storageRef = ref(storage, `dresses/${user.uid}/${Date.now()}`);
    
    try {
      await uploadBytes(storageRef, selectedImage);
      const imageUrl = await getDownloadURL(storageRef);
      
      const db = getFirestore();
      await addDoc(collection(db, 'dresses'), {
        ...newDress,
        userId: user.uid,
        imageUrl,
        createdAt: new Date().toISOString()
      });
      
      setOpenAddDress(false);
      setNewDress({
        title: '',
        description: '',
        availableDate: '',
        location: ''
      });
      setSelectedImage(null);
      await loadUserDresses(user.uid);
    } catch (error) {
      console.error('Error adding dress:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Profil */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                src={profile?.photoURL}
                sx={{ width: 150, height: 150, cursor: 'pointer' }}
                onClick={() => document.getElementById('profile-image-upload')?.click()}
              />
              <input
                type="file"
                id="profile-image-upload"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography variant="h4" gutterBottom>
              {profile?.firstName} {profile?.lastName}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {profile?.location}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating value={profile?.rating || 0} readOnly precision={0.5} />
              <Typography variant="body2" color="text.secondary">
                ({profile?.ratingCount || 0} avis)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Notes et avis */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        {user ? (
          <UserRatingStats 
            userId={user.uid} 
            userType="owner" 
            title="Mes notes et avis reçus"
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Veuillez vous connecter pour voir vos notes.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Mon Dressing */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Mon Dressing</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDress(true)}
            sx={{ backgroundColor: '#C9B6AB', '&:hover': { backgroundColor: '#C1A08E' } }}
          >
            Ajouter une robe
          </Button>
        </Box>
        <DressingGrid dresses={dresses} />
      </Paper>

      {/* Chat */}
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>Messages</Typography>
        <ChatSection userId={user?.uid} />
      </Paper>

      {/* Modal Ajouter une robe */}
      <Dialog open={openAddDress} onClose={() => setOpenAddDress(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une robe</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              component="label"
              sx={{ mb: 2 }}
            >
              Uploader une photo
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setSelectedImage(e.target.files?.[0] || null)}
              />
            </Button>
            <TextField
              label="Titre"
              value={newDress.title}
              onChange={(e) => setNewDress({ ...newDress, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={newDress.description}
              onChange={(e) => setNewDress({ ...newDress, description: e.target.value })}
              multiline
              rows={4}
              fullWidth
            />
            <TextField
              label="Date de disponibilité"
              type="date"
              value={newDress.availableDate}
              onChange={(e) => setNewDress({ ...newDress, availableDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Localisation"
              value={newDress.location}
              onChange={(e) => setNewDress({ ...newDress, location: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDress(false)}>Annuler</Button>
          <Button 
            onClick={handleAddDress}
            disabled={!selectedImage || !newDress.title || !newDress.description}
            sx={{ backgroundColor: '#C9B6AB', color: 'white', '&:hover': { backgroundColor: '#C1A08E' } }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfileScreen; 